import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useAuthStore } from '../stores/auth';
import { api } from '../utils/api';
import { Zap, Eye, EyeOff, ArrowRight } from 'lucide-react';

interface LoginForm {
  email: string;
  password: string;
}

export default function Login() {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const [showPassword, setShowPassword] = useState(false);
  
  const { register, handleSubmit, formState: { errors } } = useForm<LoginForm>();
  
  const loginMutation = useMutation({
    mutationFn: (data: LoginForm) => api.post('/auth/login', data),
    onSuccess: (response) => {
      setAuth(response.data.token, response.data.user);
      toast.success('Welcome back!');
      navigate('/');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Login failed');
    },
  });
  
  const onSubmit = (data: LoginForm) => {
    loginMutation.mutate(data);
  };
  
  return (
    <div className="min-h-screen bg-[#0b0e11] flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-[#12161c] to-[#0b0e11] relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('/grid.svg')] opacity-5" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#f0b90b]/10 rounded-full blur-[100px]" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-[#8b5cf6]/10 rounded-full blur-[80px]" />
        
        <div className="relative z-10 p-12 flex flex-col justify-center">
          <div className="flex items-center gap-3 mb-12">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#f0b90b] to-[#d9a60a] flex items-center justify-center shadow-lg glow-primary">
              <Zap className="h-7 w-7 text-[#0b0e11]" />
            </div>
            <span className="text-2xl font-bold text-white">The Continental</span>
          </div>
          
          <h1 className="text-4xl font-bold text-white mb-4 leading-tight">
            Trade smarter with<br />
            <span className="text-gradient">AI-powered automation</span>
          </h1>
          
          <p className="text-lg text-[#848e9c] max-w-md">
            The hybrid DEX/CEX platform with built-in workflow automation. 
            Trade, automate, and scale your crypto strategies.
          </p>
          
          <div className="mt-12 grid grid-cols-3 gap-6">
            <div className="card p-4">
              <div className="text-2xl font-bold text-white mb-1">$2.4B+</div>
              <div className="text-sm text-[#848e9c]">Trading Volume</div>
            </div>
            <div className="card p-4">
              <div className="text-2xl font-bold text-white mb-1">100K+</div>
              <div className="text-sm text-[#848e9c]">Active Users</div>
            </div>
            <div className="card p-4">
              <div className="text-2xl font-bold text-white mb-1">500+</div>
              <div className="text-sm text-[#848e9c]">Trading Pairs</div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Right side - Login form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#f0b90b] to-[#d9a60a] flex items-center justify-center">
              <Zap className="h-6 w-6 text-[#0b0e11]" />
            </div>
            <span className="text-2xl font-bold text-white">HybridX</span>
          </div>
          
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">Welcome back</h2>
            <p className="text-[#848e9c]">Enter your credentials to access your account</p>
          </div>
          
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-[#848e9c] mb-2">
                Email Address
              </label>
              <input
                type="email"
                {...register('email', { required: 'Email is required' })}
                className={errors.email ? 'input-error' : 'input'}
                placeholder="Enter your email"
              />
              {errors.email && (
                <p className="mt-1.5 text-xs text-[#f6465d]">{errors.email.message}</p>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-[#848e9c] mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  {...register('password', { required: 'Password is required' })}
                  className={errors.password ? 'input-error pr-12' : 'input pr-12'}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#848e9c] hover:text-white"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1.5 text-xs text-[#f6465d]">{errors.password.message}</p>
              )}
            </div>
            
            <div className="flex items-center justify-between text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 rounded border-white/20 bg-[#181a20] text-[#f0b90b] focus:ring-[#f0b90b]" />
                <span className="text-[#848e9c]">Remember me</span>
              </label>
              <Link to="/forgot-password" className="text-[#f0b90b] hover:text-[#f8d12f]">
                Forgot password?
              </Link>
            </div>
            
            <button
              type="submit"
              disabled={loginMutation.isPending}
              className="btn-primary btn-lg w-full"
            >
              {loginMutation.isPending ? (
                <span className="flex items-center gap-2">
                  <div className="spinner" />
                  Signing in...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Sign in
                  <ArrowRight className="h-5 w-5" />
                </span>
              )}
            </button>
          </form>
          
          <p className="mt-8 text-center text-[#848e9c]">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#f0b90b] hover:text-[#f8d12f] font-medium">
              Create account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
